import type { AgentSettingsData, RoutineToolMode } from '@sb/feathers-types';

import { getApp } from '../utils';

export type AgentSettings = {
  /** The operator's override, or `null` when running on the bundled default. */
  systemPrompt: string | null;
  /** The agent's bundled default prompt (read-only), for display in the editor. */
  defaultSystemPrompt: string;
  /** The operator's session-context override, or `null` on the bundled default. */
  sessionContextPrompt: string | null;
  /** The bundled session-context template (read-only), for display. */
  defaultSessionContextPrompt: string;
  /** The agent's routine-building tool surface (defaults to `dsl`). */
  routineToolMode: RoutineToolMode;
  /** Whether the agent runs its demo build flow (defaults to off). */
  poseSimulationMode: boolean;
};

/**
 * Fires with the resulting settings after any save, from any client:
 * feathers-bot publishes every service event to authenticated clients.
 */
export function subscribeAgentSettingsChanged(
  listener: (settings: AgentSettingsData) => void,
): () => void {
  const service = getApp().service('agentSettings');

  service.on('created', listener);

  return () => {
    service.removeListener('created', listener);
  };
}

/**
 * Read the agent settings: the current system-prompt override (or `null` when
 * running on the bundled default), the bundled default itself (so the editor
 * can show the real default rather than a blank box), the routine-building tool
 * mode (defaulting to `dsl`, matching the agent's own default), and the
 * pose-simulation-mode toggle (defaulting to off, likewise). The service
 * returns a single object (not a paginated list), so the find typing is
 * narrowed here.
 */
export async function getAgentSettings(): Promise<AgentSettings> {
  const result = (await getApp()
    .service('agentSettings')
    .find()) as AgentSettingsData;

  return {
    systemPrompt: result.systemPrompt ?? null,
    defaultSystemPrompt: result.defaultSystemPrompt ?? '',
    sessionContextPrompt: result.sessionContextPrompt ?? null,
    defaultSessionContextPrompt: result.defaultSessionContextPrompt ?? '',
    routineToolMode: result.routineToolMode ?? 'dsl',
    poseSimulationMode: result.poseSimulationMode ?? false,
  };
}

/**
 * Save the agent system-prompt override. Pass an empty string to clear the
 * override (reset to the bundled default). Takes effect on the agent's next
 * turn — no agent-bot restart required.
 */
export async function saveAgentSystemPrompt(prompt: string): Promise<void> {
  await getApp().service('agentSettings').create({ systemPrompt: prompt });
}

/**
 * Save the session-context template override; same sentinel and timing as
 * saveAgentSystemPrompt.
 */
export async function saveAgentSessionContextPrompt(
  prompt: string,
): Promise<void> {
  await getApp()
    .service('agentSettings')
    .create({ sessionContextPrompt: prompt });
}

/**
 * Save the agent's routine-building tool mode. Sent on its own so it never
 * clobbers the system-prompt override (the service handles each field
 * independently). Takes effect on the agent's next turn — no restart required.
 */
export async function saveAgentRoutineToolMode(
  routineToolMode: RoutineToolMode,
): Promise<void> {
  await getApp().service('agentSettings').create({ routineToolMode });
}

/**
 * Save the agent's pose-simulation-mode toggle. Sent on its own so it never
 * clobbers the system-prompt override or the tool mode (the service handles
 * each field independently). Takes effect on the agent's next turn — no restart
 * required.
 */
export async function saveAgentPoseSimulationMode(
  poseSimulationMode: boolean,
): Promise<void> {
  await getApp().service('agentSettings').create({ poseSimulationMode });
}
